/**
 * DoctypePicker — the canonical surface for doctype selection.
 *
 * Phase 6.2 (2026-04-26). Rich hierarchical picker built ground-up
 * for Variant C: domain tabs (Pharma · Medical Device · Shared) →
 * phase-grouped sections → per-doctype rows with code + authority
 * badge + framework. Replaces the conversational chip picker for
 * doctype selection across every entry path.
 *
 * Used by:
 *   - `/draft asking-doctype` step (mid-conversation pick)
 *   - ContextStrip "pick a doctype" chip-click
 *   - Any future doctype-pick command alias
 *
 * Mirrors the LibraryHub design pattern (Fluent Dialog modal, single
 * canonical surface) so writers learn one selection idiom for both
 * special-case concepts (library, doctype). Per the "writer-input
 * pattern" rule (memory feedback): everything else uses user_input;
 * library and doctype are the two named exceptions with their own
 * canonical surfaces.
 *
 * SRP: pure presentation + selection. The picker owns rendering +
 * domain-tab state + filter logic. The CALLER decides what happens
 * on select (start a draft, switch active doctype, etc.). No
 * knowledge of run state, draftFlow, libraries, or LoomFreshSelection.
 *
 * # DECISION: Show ALL catalog entries (including "coming soon"
 * #   ones without ``backendDocType``); render unsupported rows
 * #   in a disabled style with a "coming soon" hint.
 * # WHY: matches the screenshot the writer signed off on. Honest
 * #      UX — writers see the full ecosystem and which entries are
 * #      wired today. Filtering them out (the chip-picker behaviour)
 * #      hides the catalog's breadth and surprises writers when a
 * #      doctype "appears" in a later release. Honest absence beats
 * #      silent omission.
 * # DATE: 2026-04-26
 *
 * # DECISION: Expandable entries (NDA/BLA → M1-M5) render only
 * #   the parent in v1. Children deferred to a follow-up.
 * # WHY: YAGNI — most M-series children don't have ``backendDocType``
 * #      wired today, so adding the expand affordance would surface
 * #      five disabled rows that aren't actionable. When children
 * #      get wired, add the expand toggle then. Parent rows stay
 * #      visible + selectable when the parent itself is wired.
 * # DATE: 2026-04-26
 */

import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Text,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";
import React, { useMemo, useState } from "react";
import type { AppConfig } from "../../api/client";
import {
  DOC_CATALOG,
  DOMAIN_LABELS,
  type DocCatalogEntry,
  type DocCatalogPhase,
  type DocDomain,
  phasesForDomain,
} from "../doc-catalog";
import type { DoctypeChoice } from "../draftFlow";
import { brandColors, fonts, inkColors, surfaceColors } from "../tokens";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface DoctypePickerProps {
  /** Modal visibility. False = closed; true = open. */
  open: boolean;
  /** Currently-selected doctype code (highlighted in the list).
   *  Null when nothing is selected yet (idle / first pick). */
  selectedDocCode: string | null;
  /** Backend runtime config. Drives sandbox-availability filtering
   *  + the unsupported-entry "coming soon" hint. Null = treat all
   *  entries as supported (live mode, no filtering). */
  appConfig: AppConfig | null;
  /** Restrict the visible catalog to these doctype codes.
   *
   *  ``null`` / omitted = no restriction, the full catalog renders (the
   *  normal case). A non-null list narrows the picker to those codes:
   *  non-matching entries, now-empty phase groups, and now-empty domain
   *  tabs are all dropped.
   *
   *  Resolved in VariantCShell from the backend's tier-filtered
   *  ``GET /doc-types`` (:func:`useAllowedDocCodes`) — a ``free_user`` gets
   *  only the ``doctypes/doc_pricing.json`` codes, paid tiers get all. Passed
   *  ``[]`` while that load is in flight / on error, so the picker fails
   *  closed (shows nothing) rather than falling open to the full catalog.
   *  Entries are FILTERED OUT rather than disabled — the file's existing
   *  "show everything, disable what isn't wired" decision is about honest
   *  absence within the product's scope, but a reviewer looking at a page of
   *  greyed rows reads it as an unfinished add-in, which is the thing
   *  AppSource certification rejects. */
  allowedDocCodes?: readonly string[] | null;
  /** Writer picked a doctype. Caller decides what to do with the
   *  choice (start a draft, just update active, etc.). */
  onSelect: (choice: DoctypeChoice) => void;
  /** Writer cancelled (Cancel button or Escape). */
  onCancel: () => void;
  /** When true, the picker is being used to identify the type of an EXISTING
   *  document (build-on / custom submission) rather than choosing what to draft.
   *  Only changes the header copy. */
  customImport?: boolean;
}

// ---------------------------------------------------------------------------
// Pure helper — exported for tests
// ---------------------------------------------------------------------------

/** Compute whether a catalog entry is selectable in the given app
 *  config. Pure: same inputs → same output. */
export function isDoctypeAvailable(entry: DocCatalogEntry, appConfig: AppConfig | null): boolean {
  if (!entry.backendDocType) return false;
  if (!appConfig?.sandbox) return true;
  const sandboxList = appConfig.sandbox_available_doc_types ?? [];
  return sandboxList.includes(entry.backendDocType);
}

/** Phases for a domain, narrowed to ``allowedDocCodes``.
 *
 *  ``null``/undefined returns the domain's phases untouched. Otherwise
 *  each phase keeps only its matching docs, and phases left with no docs
 *  are dropped so no empty group header renders. Pure. */
export function visiblePhasesForDomain(
  domain: DocDomain,
  allowedDocCodes?: readonly string[] | null,
): DocCatalogPhase[] {
  const phases = phasesForDomain(domain);
  if (!allowedDocCodes) return phases;
  const allowed = new Set(allowedDocCodes);
  return phases
    .map((phase) => ({ ...phase, docs: phase.docs.filter((entry) => allowed.has(entry.code)) }))
    .filter((phase) => phase.docs.length > 0);
}

/** Domains that still have at least one visible doc under
 *  ``allowedDocCodes``. Used to drop domain tabs that would open onto an
 *  empty list. Pure. */
export function visibleDomains(allowedDocCodes?: readonly string[] | null): DocDomain[] {
  const domains = Object.keys(DOMAIN_LABELS) as DocDomain[];
  if (!allowedDocCodes) return domains;
  return domains.filter((domain) => visiblePhasesForDomain(domain, allowedDocCodes).length > 0);
}

/** Convert a catalog entry to the DoctypeChoice payload onSelect
 *  emits. Pure. Caller invokes only when ``isDoctypeAvailable``
 *  returned true; this helper trusts the caller to honour that. */
export function entryToChoice(entry: DocCatalogEntry, domain: DocDomain): DoctypeChoice {
  return {
    docCode: entry.code,
    backendDocType: entry.backendDocType ?? "",
    docFramework: entry.framework ?? null,
    docName: entry.name,
    domain,
    auth: entry.auth ?? null,
  };
}

/** Find which domain (pharma/device/shared) currently contains the
 *  selected doctype code. Falls back to ``"pharma"`` when not found
 *  or when no selection exists. Pure. */
export function domainForDocCode(docCode: string | null): DocDomain {
  if (!docCode) return "pharma";
  const domains: DocDomain[] = ["pharma", "device", "shared"];
  for (const domain of domains) {
    for (const phase of DOC_CATALOG[domain]) {
      for (const entry of phase.docs) {
        if (entry.code === docCode) return domain;
        if (entry.children?.some((c) => c.code === docCode)) return domain;
      }
    }
  }
  return "pharma";
}

/** Walk the catalog to recover the full ``DoctypeChoice`` payload
 *  for a stored doctype code. Pure: same inputs → same output, no
 *  React. Returns null when the code isn't in the catalog (e.g. the
 *  catalog was edited and the writer's stored selection became
 *  stale) — caller MUST treat that as "no doctype selected" rather
 *  than fabricating a partial choice.
 *
 *  Phase 7.2 — independent ``selectedDocCode`` state in the shell
 *  stores only the code string; the rest of the choice is derived
 *  here on demand. Single source of truth = the catalog. */
export function lookupDoctypeChoice(docCode: string | null): DoctypeChoice | null {
  if (!docCode) return null;
  const domains: DocDomain[] = ["pharma", "device", "shared"];
  for (const domain of domains) {
    for (const phase of DOC_CATALOG[domain]) {
      for (const entry of phase.docs) {
        if (entry.code === docCode) return entryToChoice(entry, domain);
        if (entry.children) {
          for (const child of entry.children) {
            if (child.code === docCode) return entryToChoice(child, domain);
          }
        }
      }
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  surface: {
    maxWidth: "640px",
    width: "92vw",
  },
  body: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    maxHeight: "72vh",
    overflowY: "auto",
  },
  domainTabs: {
    display: "flex",
    gap: "2px",
    borderBottom: `1px solid ${surfaceColors.line}`,
    flexShrink: 0,
  },
  domainTab: {
    flex: 1,
    padding: "8px 10px 10px",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    textAlign: "center",
    fontFamily: fonts.body,
    fontSize: "13px",
    fontWeight: 500,
    color: inkColors.ink3,
    borderBottom: "2px solid transparent",
    "&:hover": {
      color: inkColors.ink2,
    },
  },
  domainTabActive: {
    color: brandColors.primary,
    borderBottomColor: brandColors.primary,
    fontWeight: 700,
  },
  domainTabHint: {
    fontSize: "10px",
    color: inkColors.ink5,
    fontFamily: fonts.mono,
    marginTop: "2px",
    fontWeight: 500,
    letterSpacing: "0.3px",
  },
  phaseList: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  phaseLabel: {
    fontSize: "10px",
    fontWeight: 700,
    color: inkColors.ink4,
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    fontFamily: fonts.body,
    padding: "10px 4px 4px",
  },
  docRow: {
    display: "grid",
    gridTemplateColumns: "20px 1fr auto",
    alignItems: "start",
    gap: "10px",
    padding: "8px 10px",
    background: "transparent",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    width: "100%",
    textAlign: "left",
    fontFamily: fonts.body,
    "&:hover": {
      background: surfaceColors.paperDim,
    },
    "&:focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "1px",
    },
  },
  docRowDisabled: {
    cursor: "not-allowed",
    opacity: 0.5,
    "&:hover": {
      background: "transparent",
    },
  },
  docRowSelected: {
    background: brandColors.light,
  },
  docDot: {
    width: "10px",
    height: "10px",
    borderRadius: "50%",
    border: `1.5px solid ${surfaceColors.lineStrong}`,
    background: "transparent",
    marginTop: "4px",
  },
  docDotSelected: {
    width: "12px",
    height: "12px",
    background: brandColors.primary,
    border: "none",
    boxShadow: `0 0 0 3px ${brandColors.light}`,
  },
  docMain: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  docHeadRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
  },
  docCode: {
    fontSize: "14px",
    fontWeight: 700,
    color: inkColors.ink,
    fontFamily: fonts.display,
  },
  docCodeSelected: {
    color: brandColors.primary,
  },
  docName: {
    fontSize: "11.5px",
    color: inkColors.ink3,
    marginTop: "1px",
  },
  docFramework: {
    color: inkColors.ink4,
    fontFamily: fonts.mono,
    fontSize: "10.5px",
  },
  comingSoon: {
    fontSize: "9.5px",
    color: inkColors.ink4,
    fontFamily: fonts.mono,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  },
  authBadge: {
    fontSize: "9.5px",
    fontFamily: fonts.mono,
    fontWeight: 700,
  },
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const DoctypePicker: React.FC<DoctypePickerProps> = ({
  open,
  selectedDocCode,
  appConfig,
  allowedDocCodes = null,
  onSelect,
  onCancel,
  customImport = false,
}) => {
  const styles = useStyles();
  // Initial domain tab = the one containing the current selection.
  // Re-deriving on every open keeps the picker oriented when the
  // writer reopens after picking a different domain elsewhere.
  // Domain tabs that still have something behind them. Unrestricted this
  // is all three; under an allow-list it can narrow to one, or none.
  const domains = useMemo(() => visibleDomains(allowedDocCodes), [allowedDocCodes]);

  const initialDomain = useMemo(() => {
    void open; // re-orient to selected domain when picker reopens
    const preferred = domainForDocCode(selectedDocCode);
    // The selection's own domain may be filtered out by the allow-list;
    // fall back to the first tab that has content rather than opening
    // onto an empty list.
    return domains.includes(preferred) ? preferred : (domains[0] ?? "pharma");
  }, [selectedDocCode, open, domains]);
  const [domain, setDomain] = useState<DocDomain>(initialDomain);

  // Sync internal domain when the prop-derived initial changes
  // (e.g. selection changed externally while picker was closed).
  React.useEffect(() => {
    if (open) setDomain(initialDomain);
  }, [open, initialDomain]);

  // Derived rather than stored, so a mid-session narrowing of the
  // allow-list (flags resolving after mount) can't strand the picker on
  // a tab that no longer exists.
  const activeDomain = domains.includes(domain) ? domain : (domains[0] ?? "pharma");
  const phases = visiblePhasesForDomain(activeDomain, allowedDocCodes);

  const handleSelect = (entry: DocCatalogEntry): void => {
    if (!isDoctypeAvailable(entry, appConfig)) return;
    onSelect(entryToChoice(entry, activeDomain));
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(_, data) => {
        if (!data.open) onCancel();
      }}
    >
      <DialogSurface className={styles.surface}>
        <DialogBody>
          <DialogTitle>{customImport ? "Which type of document is this?" : "Pick a document type"}</DialogTitle>
          <DialogContent>
            <div className={styles.body}>
              {/* Domain tabs. Suppressed entirely when the allow-list
                  leaves one domain or none — a lone tab is chrome that
                  cannot be interacted with meaningfully. */}
              {domains.length > 1 && (
                <div className={styles.domainTabs} role="tablist" aria-label="Document domain">
                  {domains.map((key) => {
                    const meta = DOMAIN_LABELS[key];
                    const active = activeDomain === key;
                    return (
                      <button
                        type="button"
                        key={key}
                        role="tab"
                        aria-selected={active}
                        onClick={() => setDomain(key)}
                        className={mergeClasses(styles.domainTab, active && styles.domainTabActive)}
                      >
                        {meta.label}
                        <div className={styles.domainTabHint}>{meta.hint}</div>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Phase-grouped doctype rows */}
              <div className={styles.phaseList}>
                {phases.map((phase) => (
                  <div key={phase.phase}>
                    <Text className={styles.phaseLabel}>{phase.phase}</Text>
                    {phase.docs.map((entry) => {
                      const available = isDoctypeAvailable(entry, appConfig);
                      const selected = selectedDocCode !== null && entry.code === selectedDocCode;
                      return (
                        <button
                          type="button"
                          key={entry.code}
                          onClick={() => handleSelect(entry)}
                          disabled={!available}
                          aria-current={selected ? "true" : undefined}
                          className={mergeClasses(
                            styles.docRow,
                            !available && styles.docRowDisabled,
                            selected && styles.docRowSelected,
                          )}
                        >
                          <span
                            className={mergeClasses(styles.docDot, selected && styles.docDotSelected)}
                            aria-hidden="true"
                          />
                          <span className={styles.docMain}>
                            <span className={styles.docHeadRow}>
                              <span className={mergeClasses(styles.docCode, selected && styles.docCodeSelected)}>
                                {entry.code}
                              </span>
                              {entry.auth && (
                                <Badge appearance="tint" color="informative" className={styles.authBadge}>
                                  {entry.auth}
                                </Badge>
                              )}
                              {!available && <span className={styles.comingSoon}>coming soon</span>}
                            </span>
                            <span className={styles.docName}>
                              {entry.name}
                              {entry.framework && (
                                <>
                                  {" · "}
                                  <span className={styles.docFramework}>{entry.framework}</span>
                                </>
                              )}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onCancel}>
              Cancel
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default DoctypePicker;
